import {
    BaseChartConfigHelper,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/base-chart-config-helper';
import { CHART_TYPE, DisplayInvalidDataPointAs } from '@tencent/chart/lib/chart-ui/interface';
import {
    DEFAULT_SYMBOL,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/marker';
import {
    DataLabel,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/data-label';
import {
    DoubleValueAxis,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/chart-config/element/axis';
import type { Marker } from '@tencent/chart/lib/antv/g2/interface';
import type { IChartRendererConfig, IGeometryOptions } from '@tencent/chart/lib/chart-ui/interface';
import type {
    IDataLabelProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-label/data-label.interface';
import type { CustomStyles, ShapeStyle } from '../../chart-component/interface/chart-ui.interface';
import type {
    ChartUISeries,
    ChartUIDataItem,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/interface/chart-ui.interface';
import type {
    IScatterChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/scatter-chart.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import { ShapeOriginStyle } from '../../chart-component/interface/chart-ui.interface';
import {
    filterOriginStyle,
    generateCustomStyles,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils/style';

export class ScatterChartConfigHelper extends BaseChartConfigHelper {
    public get type() {
        return CHART_TYPE.SCATTER_CHART;
    }


    public getRenderConfig(): IChartRendererConfig {
        const baseConfig = super.getRenderConfig();

        return {
            ...baseConfig,
            displayInvalidDataPointAs: DisplayInvalidDataPointAs.Gap,
            showSinglePoint: false,
        };
    }

    protected normalizeSeries() {
        super.normalizeSeries();

        this.series = normalizeSeriesWithDoubleValueAxis(this.series);
    }

    // 散点图可以认为一直线条，当线条不展示时，只是线条的样式为noFill
    // 散点图底层是Line和point的组合图，两种图形共用一组数据源
    protected getGeometryOptions(): IGeometryOptions  {
        return {
            geometryStyles: this.getGeometryStyles(),
            geometryShapes: this.getGeometryShapes(),
            geometrySizes: this.getGeometrySizes(),
        };
    }

    protected get subChart(): IScatterChartProperty {
        // @ts-ignore
        return this.context.getSubChartModel()?.getSubChartProperty();
    }

    protected getDataLabel(label: IDataLabelProperty): DataLabel {
        return new ScatterDataLabel(label, this.context);
    }

    protected getAxis() {
        return new DoubleValueAxis(this.subChart, this.context.getChartModel()).init();
    }

    private get pointVisible() {
        // 取第一个ser的值，不是none就展示
        // 如果是none转出来，在marker.getConfig会是undefined
        return !!this.series[0].marker?.symbol;
    }

    private getGeometryStyles(): any {
        return {
            point: this.pointVisible ? this.createIdMappingCfg<ShapeStyle>((chartDataItem) => {
                const { marker } = chartDataItem;
                if (!marker) return {};

                return {
                    fill: marker.fill,
                    ...marker.border,
                    customStyles: this.getPointCustomStyles(marker),
                };
            }) : null,
            line: this.createFieldMappingCfg<ShapeStyle | undefined>((name) => {
                const series = this.series.find(item => item.name === name);
                if (!series) return;
                const { border } = series;

                return {
                    ...border,
                    // 有可能有smooth字段，但是不显示线的。数据上也就解不出stroke。这种情况赋值transparent，避免g2使用默认颜色
                    stroke: border?.stroke || 'transparent',
                    customStyles: this.getLineCustomStyles(series),
                };
            }, 'name'),
        };
    }

    private getPointCustomStyles(originStyle: ShapeOriginStyle): CustomStyles {
        return generateCustomStyles(originStyle);
    }

    private getLineCustomStyles(originStyle: ShapeOriginStyle): CustomStyles {
        return generateCustomStyles(originStyle);
    }

    private getGeometryShapes(): any {
        return {
            line: this.createFieldMappingCfg<string>((name) => {
                const series = this.subChart.series.find(item => item.name === name);
                return series?.smooth ? 'smooth' : 'line';
            }, 'name'),
            point: this.createIdMappingCfg<Marker | undefined>((chartDataItem) => {
                const { marker } = chartDataItem;
                return marker?.symbol || DEFAULT_SYMBOL;
            }),
        };
    }

    private getGeometrySizes() {
        return {
            point: this.createIdMappingCfg<number | undefined>((chartDataItem) => {
                if (!this.hasMarker(chartDataItem)) return 0;
                const { marker, border, serIndex } = chartDataItem;

                const lineWidth = border?.originLineWidth ? border?.lineWidth
                    : this.series[serIndex].border?.lineWidth;

                // 没有size就是"自动"选项，跟随折线宽度
                return marker?.size || lineWidth;
            }),
        };
    }
}

class ScatterDataLabel extends DataLabel {
    public static getFiledMappingConfig(data: ChartUIDataItem[], subChart: IBaseSubChartProperty) {
        const config = super.getFiledMappingConfig(data, subChart);
        (config.cfg.layout.cfg as any).minWidth = 250;

        return config;
    }
}


export function normalizeSeriesWithDoubleValueAxis(series: ChartUISeries[]) {
    return series.map(serItem => ({
        ...serItem,
        // 这里将最series中的categories也做一个转换，如果为字符串类型并且不为nil，就转成索引数字，用于坐标轴正确配置
        // 原因见 convertStringCategoryToNumber 注释
        categories: serItem.categories?.map((category, index) => {
            if (typeof category !== 'number' && !!category) {
                return index + 1;
            }
            return category;
        }),
        values: serItem.values?.map((value) => {
            if (typeof value !== 'number' && !!value) {
                return 0;
            }

            return value;
        }),
    }));
}
